import React, { useMemo } from 'react';
import { HalfLayout } from './HalfLayout';
import ResponseTypo from './ResponseTypo';
import { useSiteConfig } from '../../../hooks/useSiteConfig';
import {
  parseSiteConfigObject,
  parseSiteConfigValue,
} from '../../../api/utils/api/siteConfigApi';
import {
  SITE_CONFIG_KEY,
  SiteConfigItem,
  StoryTimelineStats,
} from '../../../constants/types/site-config-type';
import '../../../styles/story/story.css';

// API 조회 실패 시 사용할 기본값
const TEXT_FALLBACK =
  '젭스는 차원이 다른\n초레벨 바이블 영어교육을\n제공합니다.';

const MILESTONES_FALLBACK: SiteConfigItem[] = [
  { label: '2010.04', value: 'WHO MADE THE WORLD? 개발' },
  { label: '2010.10', value: '(주)젭스 법인 설립' },
  { label: '2012.05', value: '제1회 국민일보 미션어워드 수상' },
  { label: '2013.10', value: '국민일보 주최 기독교 교육 브랜드 대상 수상' },
  { label: '2015.01', value: '젭스영어성경홈스쿨 론칭' },
  { label: '2018.11', value: '젭스폰 론칭' },
  { label: '2023.06', value: '정철의 A.D.영어학습법 출간' },
  { label: '2024.08', value: '영어로 그림전도 출간' },
];

const STATS_FALLBACK: StoryTimelineStats = {
  asOf: '2023년 12월',
  items: [
    { label: '전국 교회', value: '7천 여개' },
    { label: '주일학교 코스 수료 교사', value: '1만 3천 여명' },
    { label: '홈스쿨 코스 수료 교사', value: '5천 여명' },
  ],
};

const SITE_CONFIG_KEYS = [
  SITE_CONFIG_KEY.JEBS_STORY_TIMELINE_TEXT,
  SITE_CONFIG_KEY.JEBS_STORY_TIMELINE_STATS,
  SITE_CONFIG_KEY.JEBS_STORY_TIMELINE_MILESTONES,
];

const TimeLineRow = ({ label, value }: SiteConfigItem) => {
  return (
    <div className="inline-flex h-[55px] items-center justify-start gap-[15px] lg:gap-[30px] [&:nth-last-child(1)_span]:hidden">
      <div className="w-[48px] shrink-0 font-['Pretendard'] text-sm font-normal leading-relaxed lg:w-[60px] lg:text-lg">
        {label}
      </div>
      <div className="static h-[14px] shrink-0 basis-[14px] rounded-full border-2 border-spotViolet bg-spotViolet/[0.3] lg:h-[16px] lg:basis-[16px]">
        <span>
          <div className="grayBar relative left-[4px] top-[18px] h-[29px] w-[3px] rounded-full bg-gray-300 lg:left-[5px]" />
        </span>
      </div>
      <div className="whitespace-wrap shrink grow font-['Pretendard'] text-sm font-medium leading-normal lg:text-lg">
        {value}
      </div>
    </div>
  );
};

/** 젭스 스토리 - 타임라인 영역 (헤드라인, 실적 수치, 연혁) */
export const StoryTimeLine = () => {
  const { configs } = useSiteConfig(SITE_CONFIG_KEYS);

  const text =
    configs[SITE_CONFIG_KEY.JEBS_STORY_TIMELINE_TEXT] || TEXT_FALLBACK;

  const milestones = useMemo(() => {
    const parsed = parseSiteConfigValue(
      configs[SITE_CONFIG_KEY.JEBS_STORY_TIMELINE_MILESTONES],
    );
    return parsed.length ? parsed : MILESTONES_FALLBACK;
  }, [configs]);

  // 실적 수치는 { asOf, items } 형태로 내려온다
  const { items: stats, asOf } = useMemo(() => {
    const parsed = parseSiteConfigObject<StoryTimelineStats>(
      configs[SITE_CONFIG_KEY.JEBS_STORY_TIMELINE_STATS],
    );
    return parsed?.items?.length ? parsed : STATS_FALLBACK;
  }, [configs]);

  return (
    <section className="gradient-sky p-5 lg:py-14">
      <HalfLayout
        className="m-auto max-w-[1024px] py-9 lg:px-0 lg:py-0"
        left={
          <div className="text-white">
            <ResponseTypo
              variant_sm={'h4'}
              variant_lg={'h3'}
              className="whitespace-pre-line"
            >
              {text}
            </ResponseTypo>

            {/* 실적 수치 */}
            {stats?.map((stat, index) => (
              <ResponseTypo
                key={stat.label}
                variant_sm={'h6'}
                variant_lg={'h4'}
                className={
                  index === 0
                    ? 'mt-5 font-bold lg:mt-7'
                    : 'mt-3 font-bold lg:mt-5'
                }
              >
                <span className="pr-1">{stat.label}</span>
                <span className="text-teal">{stat.value}</span>
              </ResponseTypo>
            ))}

            {/* 실적 수치 기준일 */}
            {asOf && (
              <ResponseTypo
                variant_sm={'small'}
                variant_lg={'small'}
                className="mt-3 lg:mt-6"
              >
                *{asOf} 기준
              </ResponseTypo>
            )}
          </div>
        }
        right={
          // 연혁
          <div className="radius-lg flex flex-col rounded-lg bg-white px-5 text-spotViolet lg:py-6">
            {milestones?.map((data) => (
              <TimeLineRow
                key={data.label}
                label={data.label}
                value={data.value}
              />
            ))}
          </div>
        }
      />
    </section>
  );
};
